/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { RefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useFloatingPager } from '@/hooks/useFloatingPager';

import { useKahuFloatingDock } from './kahu-floating-dock-context';
import { KahuListPager, KahuListPagerProps } from './kahu-list-pager';

/**
 * Renders the inline list pager and, when that pager scrolls out of view while
 * its list is still on screen, portals a second (floating) copy into the dock
 * above the chat input. Both copies render from the same props, so they share
 * the page state — clicking either one pages the list.
 */
export function KahuListPagerDock({
  listRef,
  ...pagerProps
}: { listRef: RefObject<HTMLElement> } & KahuListPagerProps) {
  const pagerRef = useRef<HTMLDivElement>(null);
  const { dockEl, scrollRoot } = useKahuFloatingDock();
  const shouldFloat = useFloatingPager(listRef, pagerRef, scrollRoot, true);

  return (
    <>
      <div ref={pagerRef}>
        <KahuListPager {...pagerProps} />
      </div>
      {shouldFloat &&
        dockEl &&
        createPortal(<KahuListPager {...pagerProps} floating />, dockEl)}
    </>
  );
}
